import { z } from "zod";

export const CreditUsageType = z.enum([
  "ANIMATED_IMAGE",
  "SOURCE_IMAGE",
  "VIDEO_RENDER_UPSCALE",
  "DFY_ORDER",
]);

export const CreditIncreaseType = z.enum([
  "ONE_OFF_STRIPE",
  "SUBSCRIPTION_STRIPE",
  "AMPLIFILES_GIFT",
  "ADMIN_MANUAL",
  "USER_REFERRAL",
  "REFUND",
  "COUPON",
  "EXPIRATION_MIGRATION",
  "DFY_ORDER_STRIPE",
]);

export const SubscriptionTierType = z.enum([
  "MONTHLY_29",
  "MONTHLY_49",
  "MONTHLY_99",
  "MONTHLY_149",
  "MONTHLY_249",
  "MONTHLY_499",
  "MONTHLY_799",
  "MONTHLY_1199",
]);

export const OneOffCheckoutSessionMetadata = z.object({
  checkoutType: z.literal("ONE_OFF"),
  credits: z.coerce.number(),
  price: z.coerce.number(),
  currency: z.string(),
  couponCode: z.string().optional(),
});

export const DfyOrderCheckoutSessionMetadata = z.object({
  checkoutType: z.literal("DFY_ORDER"),
  dfyOrderExternalId: z.string(),
  creditsFromBalance: z.coerce.number(),
  stripeCredits: z.coerce.number(),
  price: z.coerce.number(),
  currency: z.string(),
});

export const SubscriptionMetadata = z.object({
  subscriptionType: z.literal("MONTHLY_SUBSCRIPTION"),
  tier: SubscriptionTierType,
  credits: z.coerce.number(),
  price: z.coerce.number(),
  currency: z.string(),
  couponCode: z.string().optional(),
});

export const BillingAccountCustomPricingDto = z.object({
  creditsFirstAnimation: z.number().int().nonnegative().optional(),
  creditsReAnimation: z.number().int().nonnegative().optional(),
  numFreeReAnimations: z.number().int().nonnegative().optional(),
  creditsCustomAnimationPerSecond: z.number().int().nonnegative().optional(),
  creditsCustomImageEdit: z.number().int().nonnegative().optional(),
});

export const CreateCheckoutSessionBaseDto = z.object({
  couponCode: z.string().optional(),
  redirectVideoExternalId: z.string().optional(),
});

export const CreateOneOffCheckoutSessionDto =
  CreateCheckoutSessionBaseDto.extend({
    type: z.literal("ONE_OFF"),
    credits: z.number(),
  });

export const CreateSubscriptionCheckoutSessionDto =
  CreateCheckoutSessionBaseDto.extend({
    type: z.literal("SUBSCRIPTION"),
    tier: SubscriptionTierType,
  });

export const CreateCheckoutSessionDto = z.discriminatedUnion("type", [
  CreateOneOffCheckoutSessionDto,
  CreateSubscriptionCheckoutSessionDto,
]);

export const CheckoutSessionDto = z.object({
  url: z.string(),
});

export const CreditIncreaseDto = z.object({
  externalId: z.string(),
  createdAt: z.string(),
  credits: z.number(),
  price: z.number().nullable(),
  currency: z.string().nullable(),
});

export const CustomerPortalDto = z.object({
  url: z.string(),
});

export const SyncBillingResponseDto = z.object({
  status: z.literal("ok"),
});

export type CreditUsageType = z.infer<typeof CreditUsageType>;
export type CreditIncreaseType = z.infer<typeof CreditIncreaseType>;
export type OneOffCheckoutSessionMetadata = z.infer<
  typeof OneOffCheckoutSessionMetadata
>;
export type DfyOrderCheckoutSessionMetadata = z.infer<
  typeof DfyOrderCheckoutSessionMetadata
>;
export type SubscriptionMetadata = z.infer<typeof SubscriptionMetadata>;
export type BillingAccountCustomPricingDto = z.infer<
  typeof BillingAccountCustomPricingDto
>;
export type SubscriptionTierType = z.infer<typeof SubscriptionTierType>;
export type CreateCheckoutSessionBaseDto = z.infer<
  typeof CreateCheckoutSessionBaseDto
>;
export type CreateOneOffCheckoutSessionDto = z.infer<
  typeof CreateOneOffCheckoutSessionDto
>;
export type CreateSubscriptionCheckoutSessionDto = z.infer<
  typeof CreateSubscriptionCheckoutSessionDto
>;
export type CreateCheckoutSessionDto = z.infer<typeof CreateCheckoutSessionDto>;
export type CheckoutSessionDto = z.infer<typeof CheckoutSessionDto>;
export type CreditIncreaseDto = z.infer<typeof CreditIncreaseDto>;
export type CustomerPortalDto = z.infer<typeof CustomerPortalDto>;
export type SyncBillingResponseDto = z.infer<typeof SyncBillingResponseDto>;
